import type {
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodePosition,
  WorkspaceNodeType,
} from "../../../types/workspace";
import {
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_GRAPH_BRANCH_GAP,
  WORKSPACE_GRAPH_COLUMN_GAP,
  WORKSPACE_GRAPH_ORIGIN_X,
  WORKSPACE_GRAPH_ORIGIN_Y,
  WORKSPACE_GRAPH_ROW_GAP,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
  WORKSPACE_NODE_COLLISION_GAP,
  WORKSPACE_NODE_SNAP_GRID,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  getWorkspaceNodeDimensions,
  snapWorkspaceNodeByCenter,
} from "./workspaceGraphConstants";
import { workspaceNodeShowsForwardPlaceholder } from "./workspacePlaceholderPolicy";
import { WORKSPACE_MULTI_PARENT_TYPES } from "./workspaceReparentUtils";
import { resolveEntityBrokerAccounts } from "../../../utils/brokerAccountRef";

const ENTITY_CARD_SIZE = {
  width: WORKSPACE_NODE_CARD_WIDTH,
  height: WORKSPACE_NODE_CARD_HEIGHT,
};

/** Entity cards snap by center so they share the same lattice as account / + hubs. */
function snapEntityCardPosition(position: WorkspaceNodePosition): WorkspaceNodePosition {
  return snapWorkspaceNodeByCenter(position, ENTITY_CARD_SIZE);
}

function snapEntityCardX(x: number): number {
  return snapEntityCardPosition({ x, y: 0 }).x;
}

/**
 * Placeholders must keep an exact spine Y so edges stay purely orthogonal.
 * Only X is lattice-snapped into the child column.
 */
function snapPlaceholderPosition(
  position: WorkspaceNodePosition,
  size: { width: number; height: number }
): WorkspaceNodePosition {
  const snappedX = snapWorkspaceNodeByCenter(
    { x: position.x, y: position.y },
    size
  ).x;
  return { x: snappedX, y: position.y };
}

/** Center-align a child card on the parent's horizontal spine, then lattice-snap. */
function alignChildToParentSpine(
  parent: WorkspaceNode,
  childX: number,
  childHeight: number = WORKSPACE_NODE_CARD_HEIGHT
): WorkspaceNodePosition {
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { height: parentHeight } = getWorkspaceNodeDimensions(parent);
  return snapEntityCardPosition({
    x: childX,
    y: parentPosition.y + parentHeight / 2 - childHeight / 2,
  });
}

const PIPELINE_TYPE_ORDER: WorkspaceNodeType[] = [
  "account",
  "strategy",
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
];

export function countChildNodes(parentNodeId: string, edges: WorkspaceEdge[]): number {
  return edges.filter((edge) => edge.sourceNodeId === parentNodeId).length;
}

export function resolveDefaultAccountPosition(): WorkspaceNodePosition {
  return snapWorkspaceNodeByCenter(
    {
      x: WORKSPACE_GRAPH_ORIGIN_X,
      y: WORKSPACE_GRAPH_ORIGIN_Y - WORKSPACE_ACCOUNT_CARD_SIZE / 2,
    },
    { width: WORKSPACE_ACCOUNT_CARD_SIZE, height: WORKSPACE_ACCOUNT_CARD_SIZE }
  );
}

/** Account nodes saved at the API default origin are laid out on the pipeline row. */
export function resolveWorkspaceNodePosition(node: WorkspaceNode): WorkspaceNodePosition {
  const { x, y } = node.position;
  if (node.type === "account" && x === 0 && y === 0) {
    return resolveDefaultAccountPosition();
  }
  return node.position;
}

/**
 * Prefer the X of existing direct siblings so new cards stay on the same column
 * (parent+gap math can miss the snap lattice Organize layout already used).
 */
function resolveChildColumnX(
  siblingPositions: WorkspaceNodePosition[],
  fallbackX: number
): number {
  if (siblingPositions.length === 0) return snapEntityCardX(fallbackX);

  const counts = new Map<number, number>();
  for (const position of siblingPositions) {
    const x = Math.round(position.x);
    counts.set(x, (counts.get(x) ?? 0) + 1);
  }
  let bestX = siblingPositions[0]!.x;
  let bestCount = 0;
  Array.from(counts.entries()).forEach(([x, count]) => {
    if (count > bestCount) {
      bestCount = count;
      bestX = x;
    }
  });
  return bestX;
}

export function computeChildNodePosition(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  childHeight: number = WORKSPACE_NODE_CARD_HEIGHT
): WorkspaceNodePosition {
  const siblingIndex = countChildNodes(parent.id, edges);
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { width: parentWidth } = getWorkspaceNodeDimensions(parent);
  const childX = snapEntityCardX(
    parentPosition.x + parentWidth + WORKSPACE_GRAPH_COLUMN_GAP
  );

  if (siblingIndex === 0) {
    return alignChildToParentSpine(parent, childX, childHeight);
  }

  const gap =
    parent.type === "account" ? WORKSPACE_GRAPH_BRANCH_GAP : WORKSPACE_GRAPH_ROW_GAP;
  // Stack from the parent spine, then lattice-snap so every sibling shares centers.
  const first = alignChildToParentSpine(parent, childX, childHeight);
  return snapEntityCardPosition({
    x: childX,
    y: first.y + siblingIndex * (childHeight + gap),
  });
}

/**
 * Place a new child under the lowest existing sibling so we never land on an occupied
 * card slot (index-based packing can collide after users drag nodes around).
 */
/**
 * Place a multi-parent merge node (combined_policy) on the mean vertical spine
 * of its source parents, just to the right of the rightmost parent.
 */
export function computeMultiParentMergePosition(
  parents: WorkspaceNode[],
  childHeight: number = WORKSPACE_NODE_CARD_HEIGHT
): WorkspaceNodePosition {
  if (parents.length === 0) {
    return snapEntityCardPosition({ x: WORKSPACE_GRAPH_ORIGIN_X, y: WORKSPACE_GRAPH_ORIGIN_Y });
  }
  if (parents.length === 1) {
    const parent = parents[0]!;
    const parentPosition = resolveWorkspaceNodePosition(parent);
    const { width: parentWidth } = getWorkspaceNodeDimensions(parent);
    return alignChildToParentSpine(
      parent,
      snapEntityCardX(parentPosition.x + parentWidth + WORKSPACE_GRAPH_COLUMN_GAP),
      childHeight
    );
  }

  let sumCenter = 0;
  let maxRight = -Infinity;
  for (const parent of parents) {
    const pos = resolveWorkspaceNodePosition(parent);
    const { width, height } = getWorkspaceNodeDimensions(parent);
    sumCenter += pos.y + height / 2;
    maxRight = Math.max(maxRight, pos.x + width);
  }
  const targetCenter = sumCenter / parents.length;
  return snapEntityCardPosition({
    x: snapEntityCardX(maxRight + WORKSPACE_GRAPH_COLUMN_GAP),
    y: targetCenter - childHeight / 2,
  });
}

/**
 * Stack a simulation rerun under its parent simulation (same column X).
 */
export function computeRerunSimulationPosition(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): WorkspaceNodePosition {
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { height: parentHeight } = getWorkspaceNodeDimensions(parent);
  const nodeById = new Map(allNodes.map((node) => [node.id, node]));
  const stackedType =
    parent.type === "combined_simulation" ? "combined_simulation" : "simulation";
  let lowestBottom = parentPosition.y + parentHeight;

  for (const edge of edges) {
    if (edge.sourceNodeId !== parent.id) continue;
    const child = nodeById.get(edge.targetNodeId);
    if (!child || child.type !== stackedType) continue;
    const position = resolveWorkspaceNodePosition(child);
    const { height } = getWorkspaceNodeDimensions(child);
    lowestBottom = Math.max(lowestBottom, position.y + height);
  }

  return snapEntityCardPosition({
    x: parentPosition.x,
    y: lowestBottom + WORKSPACE_GRAPH_ROW_GAP,
  });
}

export function computeAppendChildNodePosition(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): WorkspaceNodePosition {
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { width: parentWidth } = getWorkspaceNodeDimensions(parent);
  const fallbackX = parentPosition.x + parentWidth + WORKSPACE_GRAPH_COLUMN_GAP;
  const gap =
    parent.type === "account" ? WORKSPACE_GRAPH_BRANCH_GAP : WORKSPACE_GRAPH_ROW_GAP;

  const childIds = edges
    .filter((edge) => edge.sourceNodeId === parent.id)
    .map((edge) => edge.targetNodeId);

  if (childIds.length === 0) {
    return alignChildToParentSpine(parent, snapEntityCardX(fallbackX));
  }

  const nodeById = new Map(allNodes.map((node) => [node.id, node]));
  const siblingPositions: WorkspaceNodePosition[] = [];
  let lowestBottom = -Infinity;
  for (const childId of childIds) {
    const child = nodeById.get(childId);
    if (!child) continue;
    const position = resolveWorkspaceNodePosition(child);
    siblingPositions.push(position);
    // Use each sibling's full subtree bottom so a report with sims/policies
    // reserves the same band Organize layout would.
    const extent = measureSubtreeVerticalExtent(childId, allNodes, edges, nodeById);
    if (!extent) {
      const { height } = getWorkspaceNodeDimensions(child);
      lowestBottom = Math.max(lowestBottom, position.y + height);
      continue;
    }
    lowestBottom = Math.max(lowestBottom, extent.bottom);
  }

  const childX = resolveChildColumnX(siblingPositions, fallbackX);

  if (!Number.isFinite(lowestBottom)) {
    return alignChildToParentSpine(parent, childX);
  }

  return snapEntityCardPosition({
    x: childX,
    y: lowestBottom + gap,
  });
}

function buildChildrenByParent(
  edges: WorkspaceEdge[],
  nodeById?: Map<string, WorkspaceNode>
): Map<string, string[]> {
  const parentsByChild = new Map<string, string[]>();
  if (nodeById) {
    for (const edge of edges) {
      const list = parentsByChild.get(edge.targetNodeId) ?? [];
      if (!list.includes(edge.sourceNodeId)) list.push(edge.sourceNodeId);
      parentsByChild.set(edge.targetNodeId, list);
    }
  }

  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    if (nodeById) {
      const child = nodeById.get(edge.targetNodeId);
      if (child && WORKSPACE_MULTI_PARENT_TYPES.has(child.type)) {
        const primary = parentsByChild.get(edge.targetNodeId)?.[0];
        if (primary !== edge.sourceNodeId) continue;
      }
    }
    const list = childrenByParent.get(edge.sourceNodeId) ?? [];
    if (!list.includes(edge.targetNodeId)) list.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, list);
  }
  return childrenByParent;
}

function gapBetweenSiblingBranches(parent: WorkspaceNode): number {
  return parent.type === "account" ? WORKSPACE_GRAPH_BRANCH_GAP : WORKSPACE_GRAPH_ROW_GAP;
}

export function measureSubtreeVerticalExtent(
  rootId: string,
  allNodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  nodeById = new Map(allNodes.map((node) => [node.id, node])),
  childrenByParent = buildChildrenByParent(edges, nodeById)
): { top: number; bottom: number } | null {
  const root = nodeById.get(rootId);
  if (!root) return null;

  let top = Infinity;
  let bottom = -Infinity;
  const stack = [rootId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodeById.get(id);
    if (!node) continue;
    const position = resolveWorkspaceNodePosition(node);
    const { height } = getWorkspaceNodeDimensions(node);
    top = Math.min(top, position.y);
    bottom = Math.max(bottom, position.y + height);
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }

  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null;
  return { top, bottom };
}

/**
 * Subtree band plus the forward “Add …” diamond / executing-account stack when
 * this root shows one. Used so sibling branches leave room instead of stacking
 * on ephemeral diamonds.
 */
export function measureSubtreeVerticalExtentWithPlaceholders(
  rootId: string,
  allNodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  nodeById = new Map(allNodes.map((node) => [node.id, node])),
  childrenByParent = buildChildrenByParent(edges, nodeById)
): { top: number; bottom: number } | null {
  const base = measureSubtreeVerticalExtent(
    rootId,
    allNodes,
    edges,
    nodeById,
    childrenByParent
  );
  if (!base) return null;

  let top = base.top;
  let bottom = base.bottom;
  const stack = [rootId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodeById.get(id);
    if (!node) continue;
    const position = resolveWorkspaceNodePosition(node);
    const { height } = getWorkspaceNodeDimensions(node);
    if (node.type === "policy" || node.type === "combined_policy") {
      const accountCount = resolveEntityBrokerAccounts({
        accounts: node.meta?.accounts,
      }).length;
      if (accountCount > 0) {
        const eaHeight =
          accountCount * WORKSPACE_NODE_CARD_HEIGHT +
          Math.max(0, accountCount - 1) * WORKSPACE_GRAPH_ROW_GAP;
        const centerY = position.y + height / 2;
        top = Math.min(top, centerY - eaHeight / 2);
        bottom = Math.max(bottom, centerY + eaHeight / 2);
      }
    }
    if (workspaceNodeShowsForwardPlaceholder(node, edges, allNodes)) {
      const placeholder = computePlaceholderNodePosition(
        node,
        edges,
        WORKSPACE_PLACEHOLDER_NODE_SIZE,
        allNodes
      );
      bottom = Math.max(bottom, placeholder.y + WORKSPACE_PLACEHOLDER_NODE_SIZE);
    }
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }

  return { top, bottom };
}

/**
 * After a branch grows (new report under a strategy, etc.), push lower sibling
 * branches down so account/ROW gaps stay tidy — same spacing Organize layout uses.
 * Only moves nodes downward; never pulls custom layouts upward.
 */
export function enforceSiblingBranchGaps(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): {
  nodes: WorkspaceNode[];
  shifts: Array<{ nodeId: string; x: number; y: number }>;
} {
  if (nodes.length <= 1 || edges.length === 0) {
    return { nodes, shifts: [] };
  }

  const childrenByParent = buildChildrenByParent(
    edges,
    new Map(nodes.map((node) => [node.id, node]))
  );
  const working = new Map(
    nodes.map((node) => [
      node.id,
      {
        ...node,
        position: { ...resolveWorkspaceNodePosition(node) },
      },
    ])
  );

  const shiftSubtree = (rootId: string, dy: number) => {
    if (Math.abs(dy) <= 0.5) return;
    const stack = [rootId];
    const seen = new Set<string>();
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = working.get(id);
      if (!node) continue;
      node.position = { x: node.position.x, y: node.position.y + dy };
      for (const childId of childrenByParent.get(id) ?? []) {
        stack.push(childId);
      }
    }
  };

  // Left-to-right parents first (account → strategy → report) so outer branch
  // shifts cascade before we tidy nested stacks.
  const parents = nodes
    .filter((node) => (childrenByParent.get(node.id) ?? []).length > 0)
    .sort((a, b) => {
      const aPos = resolveWorkspaceNodePosition(a);
      const bPos = resolveWorkspaceNodePosition(b);
      if (aPos.x !== bPos.x) return aPos.x - bPos.x;
      return aPos.y - bPos.y;
    });

  for (const parent of parents) {
    const liveParent = working.get(parent.id);
    if (!liveParent) continue;
    const childIds = childrenByParent.get(parent.id) ?? [];
    const children = childIds
      .map((id) => working.get(id))
      .filter((node): node is WorkspaceNode => Boolean(node))
      .sort((a, b) => {
        const aExtent = measureSubtreeVerticalExtentWithPlaceholders(
          a.id,
          Array.from(working.values()),
          edges,
          working,
          childrenByParent
        );
        const bExtent = measureSubtreeVerticalExtentWithPlaceholders(
          b.id,
          Array.from(working.values()),
          edges,
          working,
          childrenByParent
        );
        const aTop = aExtent?.top ?? a.position.y;
        const bTop = bExtent?.top ?? b.position.y;
        if (aTop !== bTop) return aTop - bTop;
        return a.id.localeCompare(b.id);
      });

    if (children.length <= 1) continue;

    const gap = gapBetweenSiblingBranches(liveParent);
    for (let index = 1; index < children.length; index += 1) {
      const previous = children[index - 1]!;
      const current = children[index]!;
      const previousExtent = measureSubtreeVerticalExtentWithPlaceholders(
        previous.id,
        Array.from(working.values()),
        edges,
        working,
        childrenByParent
      );
      const currentExtent = measureSubtreeVerticalExtentWithPlaceholders(
        current.id,
        Array.from(working.values()),
        edges,
        working,
        childrenByParent
      );
      if (!previousExtent || !currentExtent) continue;

      const requiredTop = previousExtent.bottom + gap;
      const dy = requiredTop - currentExtent.top;
      // Push down when overlapping; pull up when the corridor is emptier than the
      // intended branch/row gap (collapse holes with no cards in between).
      if (Math.abs(dy) > 0.5) {
        shiftSubtree(current.id, dy);
      }
    }
  }

  const nextNodes = nodes.map((node) => {
    const updated = working.get(node.id);
    return updated ?? node;
  });

  const shifts: Array<{ nodeId: string; x: number; y: number }> = [];
  for (const node of nodes) {
    const updated = working.get(node.id);
    if (!updated) continue;
    const before = resolveWorkspaceNodePosition(node);
    if (
      Math.abs(before.x - updated.position.x) > 0.5 ||
      Math.abs(before.y - updated.position.y) > 0.5
    ) {
      shifts.push({
        nodeId: updated.id,
        x: updated.position.x,
        y: updated.position.y,
      });
    }
  }

  return { nodes: nextNodes, shifts };
}

/** Apply branch-gap enforcement to a workspace detail graph. */
export function withEnforcedSiblingBranchGaps<T extends { nodes: WorkspaceNode[]; edges: WorkspaceEdge[] }>(
  detail: T
): {
  detail: T;
  shifts: Array<{ nodeId: string; x: number; y: number }>;
} {
  const { nodes, shifts } = enforceSiblingBranchGaps(detail.nodes, detail.edges);
  return {
    detail: { ...detail, nodes },
    shifts,
  };
}

/**
 * Diamond placeholder sits in the next pipeline column (same runway as real
 * next-step cards). Empty parents: exact parent spine Y (straight horizontal).
 * Parents with next-step siblings: same column, exactly one row-gap below the
 * lowest sibling card — same spacing language as card stacks.
 */
export function computePlaceholderNodePosition(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  placeholderSize: number,
  allNodes: WorkspaceNode[] = []
): WorkspaceNodePosition {
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { width: parentWidth, height: parentHeight } = getWorkspaceNodeDimensions(parent);
  const size = { width: placeholderSize, height: placeholderSize };
  const nextKind: WorkspaceNodeType | null =
    parent.type === "strategy"
      ? "report"
      : parent.type === "report"
        ? "simulation"
        : parent.type === "combined_policy"
          ? "combined_simulation"
          : null;

  const nodeById = new Map(allNodes.map((node) => [node.id, node]));
  const siblings = edges
    .filter((edge) => edge.sourceNodeId === parent.id)
    .map((edge) => nodeById.get(edge.targetNodeId))
    .filter((node): node is WorkspaceNode => {
      if (!node) return false;
      if (!nextKind) return true;
      return node.type === nextKind;
    });

  const fallbackColumnX = parentPosition.x + parentWidth + WORKSPACE_GRAPH_COLUMN_GAP;
  const columnX =
    siblings.length > 0
      ? resolveChildColumnX(
          siblings.map((sibling) => resolveWorkspaceNodePosition(sibling)),
          fallbackColumnX
        )
      : fallbackColumnX;
  // Center the smaller diamond within the entity card column.
  const centeredX = columnX + (WORKSPACE_NODE_CARD_WIDTH - placeholderSize) / 2;

  if (siblings.length === 0) {
    return snapPlaceholderPosition(
      {
        x: centeredX,
        // Exact spine match → pure horizontal edge (no kink from Y snap).
        y: parentPosition.y + parentHeight / 2 - placeholderSize / 2,
      },
      size
    );
  }

  let lowestBottom = -Infinity;
  for (const sibling of siblings) {
    // Sibling card only — parking under the full sim→policy subtree pushed the
    // diamond into awkward positions far below the next-step column.
    const position = resolveWorkspaceNodePosition(sibling);
    const { height } = getWorkspaceNodeDimensions(sibling);
    lowestBottom = Math.max(lowestBottom, position.y + height);
  }

  if (!Number.isFinite(lowestBottom)) {
    return snapPlaceholderPosition(
      {
        x: centeredX,
        y: parentPosition.y + parentHeight / 2 - placeholderSize / 2,
      },
      size
    );
  }

  return snapPlaceholderPosition(
    {
      x: centeredX,
      // Exact row gap — same minimum spacing as between stacked entity cards.
      y: lowestBottom + WORKSPACE_GRAPH_ROW_GAP,
    },
    size
  );
}

/** True when two or more nodes share the same rounded grid position (common after create). */
export function workspaceNodesNeedAutoLayout(nodes: WorkspaceNode[]): boolean {
  if (nodes.length <= 1) return false;

  // Backend defaults new nodes to {0,0} when no position is sent (e.g. policy save).
  for (const node of nodes) {
    if (node.type === "account") continue;
    if (node.position.x === 0 && node.position.y === 0) return true;
  }

  const buckets = new Set<string>();
  for (const node of nodes) {
    const position = resolveWorkspaceNodePosition(node);
    const key = `${Math.round(position.x / 8)}:${Math.round(position.y / 8)}`;
    if (buckets.has(key)) return true;
    buckets.add(key);
  }

  if (workspaceGraphHasExcessiveVerticalGaps(nodes)) return true;
  return workspaceNodesHaveOverlap(nodes);
}

/**
 * True when any pipeline column has occupied cards separated by more than
 * ~one card of empty space — including holes that other columns still fill
 * (e.g. empty sim corridor beside a policy stack).
 */
export function workspaceGraphHasExcessiveVerticalGaps(
  nodes: WorkspaceNode[],
  maxGap: number = WORKSPACE_NODE_CARD_HEIGHT + WORKSPACE_GRAPH_ROW_GAP
): boolean {
  if (nodes.length <= 1) return false;

  const byColumn = new Map<number, Array<{ top: number; bottom: number }>>();
  for (const node of nodes) {
    const position = resolveWorkspaceNodePosition(node);
    const { height } = getWorkspaceNodeDimensions(node);
    const key =
      Math.round(position.x / WORKSPACE_NODE_SNAP_GRID) * WORKSPACE_NODE_SNAP_GRID;
    const list = byColumn.get(key) ?? [];
    list.push({ top: position.y, bottom: position.y + height });
    byColumn.set(key, list);
  }

  for (const items of Array.from(byColumn.values())) {
    if (items.length <= 1) continue;
    const ordered = [...items].sort((a, b) => a.top - b.top);
    let bandBottom = ordered[0]!.bottom;
    for (let index = 1; index < ordered.length; index += 1) {
      const item = ordered[index]!;
      if (item.top <= bandBottom + 0.5) {
        bandBottom = Math.max(bandBottom, item.bottom);
        continue;
      }
      if (item.top - bandBottom > maxGap) return true;
      bandBottom = item.bottom;
    }
  }
  return false;
}

/**
 * True when any two node bounding boxes collide (including the drag collision gap).
 * Used after node create / refresh: overlap → run Organize layout.
 */
export function workspaceNodesHaveOverlap(
  nodes: WorkspaceNode[],
  gap: number = WORKSPACE_NODE_COLLISION_GAP
): boolean {
  if (nodes.length <= 1) return false;

  const boxes = nodes.map((node) => {
    const position = resolveWorkspaceNodePosition(node);
    const { width, height } = getWorkspaceNodeDimensions(node);
    return { id: node.id, x: position.x, y: position.y, width, height };
  });

  for (let i = 0; i < boxes.length; i += 1) {
    const a = boxes[i]!;
    for (let j = i + 1; j < boxes.length; j += 1) {
      const b = boxes[j]!;
      if (
        a.x < b.x + b.width + gap &&
        a.x + a.width + gap > b.x &&
        a.y < b.y + b.height + gap &&
        a.y + a.height + gap > b.y
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Stricter than {@link workspaceNodesNeedAutoLayout}: true only when the graph
 * looks like a fresh / dumped layout. Used on workspace enter so one stray
 * `{0,0}` node does not rewrite a user's custom arrangement.
 */
export function workspaceGraphLooksUninitialized(nodes: WorkspaceNode[]): boolean {
  if (nodes.length <= 1) return false;

  const nonAccount = nodes.filter((node) => node.type !== "account");
  if (nonAccount.length === 0) return false;

  const originCount = nonAccount.filter(
    (node) => node.position.x === 0 && node.position.y === 0
  ).length;
  if (originCount >= Math.max(2, Math.ceil(nonAccount.length / 2))) {
    return true;
  }

  const buckets = new Map<string, number>();
  for (const node of nodes) {
    const position = resolveWorkspaceNodePosition(node);
    const key = `${Math.round(position.x / 8)}:${Math.round(position.y / 8)}`;
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  const maxStack = Array.from(buckets.values()).reduce((max, count) => Math.max(max, count), 0);
  return maxStack >= Math.max(3, Math.ceil(nodes.length / 2));
}

/**
 * Place non-account nodes still stuck at `{0,0}` next to their parent without
 * reflowing the rest of the canvas.
 */
export function placeWorkspaceOriginOrphanPositions(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Array<{ nodeId: string; x: number; y: number }> {
  if (nodes.length === 0) return [];

  const working = nodes.map((node) => ({
    ...node,
    position: { ...resolveWorkspaceNodePosition(node) },
  }));
  const workingById = new Map(working.map((node) => [node.id, node]));
  const shifts: Array<{ nodeId: string; x: number; y: number }> = [];

  for (const node of working) {
    if (node.type === "account") continue;
    if (node.position.x !== 0 || node.position.y !== 0) continue;

    const parentIds = edges
      .filter((edge) => edge.targetNodeId === node.id)
      .map((edge) => edge.sourceNodeId);
    const parents = parentIds
      .map((id) => workingById.get(id))
      .filter((entry): entry is WorkspaceNode => Boolean(entry));

    const { height } = getWorkspaceNodeDimensions(node);
    let nextPosition: WorkspaceNodePosition;
    if (parents.length > 1) {
      nextPosition = computeMultiParentMergePosition(parents, height);
    } else if (parents.length === 1) {
      const parent = parents[0]!;
      const edgesWithoutSelf = edges.filter((edge) => edge.targetNodeId !== node.id);
      nextPosition = computeChildNodePosition(parent, edgesWithoutSelf, height);
    } else {
      continue;
    }

    node.position = nextPosition;
    shifts.push({ nodeId: node.id, x: nextPosition.x, y: nextPosition.y });
  }

  return shifts;
}

function expectedChildX(parent: WorkspaceNode): number {
  const position = resolveWorkspaceNodePosition(parent);
  const { width } = getWorkspaceNodeDimensions(parent);
  return snapEntityCardX(position.x + width + WORKSPACE_GRAPH_COLUMN_GAP);
}

/** True when saved positions do not match the left-to-right pipeline layout.
 * Used for diagnostics / create placement — not for on-load auto-layout
 * (that uses `workspaceNodesNeedAutoLayout` so custom layouts persist).
 */
export function workspaceGraphNeedsLayout(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
): boolean {
  if (nodes.length <= 1) return false;
  if (workspaceNodesNeedAutoLayout(nodes)) return true;

  if (edges.length === 0) return true;

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  for (const edge of edges) {
    const source = nodeById.get(edge.sourceNodeId);
    const target = nodeById.get(edge.targetNodeId);
    if (!source || !target) continue;

    const targetPosition = resolveWorkspaceNodePosition(target);
    const expectedX = expectedChildX(source);
    if (Math.abs(targetPosition.x - expectedX) > 16) return true;
  }

  const sortedByType = [...nodes].sort(
    (a, b) => PIPELINE_TYPE_ORDER.indexOf(a.type) - PIPELINE_TYPE_ORDER.indexOf(b.type)
  );
  for (let index = 1; index < sortedByType.length; index += 1) {
    const previous = sortedByType[index - 1]!;
    const current = sortedByType[index]!;
    const previousPosition = resolveWorkspaceNodePosition(previous);
    const currentPosition = resolveWorkspaceNodePosition(current);
    if (currentPosition.x <= previousPosition.x + 8) return true;
  }

  return false;
}
